import React, { cloneElement, useId, useRef } from 'react';
import { UncontrolledTooltip } from 'reactstrap';

const sanitize_dom_id = (raw_id) => (
    String(raw_id || '')
        .replace(/[[\]./]/g, '-')
        .replace(/[^a-zA-Z0-9_-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
);

const merge_ref = (existing, node) => {
    if (typeof existing === 'function')
        existing(node);
    else if (existing && typeof existing === 'object')
        existing.current = node;
};

/**
 * Accessible label + Bootstrap tooltip for a single icon control.
 * Do not pass a native `title` on the child: the browser would show a second tooltip.
 * Pass a ref to reactstrap: string targets use document.querySelector and miss
 * nodes that are not attached to document (Jest smoke tests).
 */
const IconTooltip = ({
    id,
    label,
    children,
    placement = 'top',
    enabled = true,
}) => {
    const generatedId = useId().replace(/:/g, '');
    const requestedId = sanitize_dom_id(id);
    const fallbackId = requestedId || `icon-tooltip-${generatedId}`;
    const targetRef = useRef(null);

    if (!React.isValidElement(children))
        return children || null;

    const targetId = children.props.id || fallbackId;
    const isHostElement = typeof children.type === 'string';
    const labeledChild = cloneElement(children, {
        id: targetId,
        'aria-label': children.props['aria-label'] ?? (label || undefined),
        ...(isHostElement
            ? {
                ref: (node) => {
                    targetRef.current = node;
                    merge_ref(children.ref, node);
                },
            }
            : {
                innerRef: (node) => {
                    targetRef.current = node;
                    merge_ref(children.props.innerRef, node);
                },
            }),
    });

    return (
        <>
            {labeledChild}
            {enabled && label && (
                <UncontrolledTooltip target={targetRef} placement={placement}>
                    {label}
                </UncontrolledTooltip>
            )}
        </>
    );
};

export default IconTooltip;
export { sanitize_dom_id };
